<script setup lang="ts">
import type { OrthancStudyInfo } from '@/src/core/orthanc-api';

import { computed, onMounted, ref, watch } from 'vue';

import { useAlert } from '@/src/composables/useAlert';
import { useOrthancStore } from '@/src/store/dicom-web/orthanc-store';
import { supabase } from '@/src/supabase';
import { getMessage } from '@/src/utils/exception';

const emit = defineEmits<{
  error: [message: string];
}>();

const alert = useAlert();
const orthancStore = useOrthancStore();

export interface AnalysisSaveItem {
  id: string;
  study_instance_uid: string;
  user: string;
  userName: string;
  userEmail: string;
  org: string;
  orgName: string;
  title: string;
  created_at: string;
}

export interface StudyItem {
  studyInstanceUid: string;
  orthancId: string;
  patientName: string;
  patientId: string;
  studyDate: string;
  studyTime: string;
  studyDescription: string;
  modality: string;
  lastUpdate: string;
  lastUpdateMs: number;
  saves: AnalysisSaveItem[];
  savesCount: number;
  isLoadingSaves: boolean;
}

const busy = ref(false);
const isDeleting = ref(false);

// Sort order: 'newest' (Most Recent first) or 'oldest' (Oldest first) based on Import Date
const sortOrder = ref<'newest' | 'oldest'>('newest');

// Pagination: 5 studies per page
const PAGE_SIZE = 5;
const currentPage = ref(1);

const allDicomStudies = ref<StudyItem[]>([]);
const savesByStudyUid = ref<Map<string, AnalysisSaveItem[]>>(new Map());

const selectedStudyUid = ref<string | null>(null);
const selectedSaveIds = ref<Set<string>>(new Set());

// Confirmation Dialogs
const dialogDeleteSingle = ref(false);
const saveToDelete = ref<AnalysisSaveItem | null>(null);

const dialogDeleteBatch = ref(false);
const dialogDeleteStudy = ref(false);
const dialogDeleteEntireStudy = ref(false);
const studyToDelete = ref<StudyItem | null>(null);

function parseOrthancDate(v?: string): number {
  if (!v) return 0;
  const m = v.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (m) {
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  }
  const d = new Date(v).getTime();
  return Number.isNaN(d) ? 0 : d;
}

function formatImportDate(v?: string): string {
  const ms = parseOrthancDate(v);
  if (!ms) return '-';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDicomDate(dateStr?: string): string {
  if (!dateStr || dateStr.length !== 8) return dateStr || '-';
  const y = dateStr.slice(0, 4);
  const m = dateStr.slice(4, 6);
  const d = dateStr.slice(6, 8);
  return `${y}-${m}-${d}`;
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  catch {
    return iso;
  }
}

function flattenStudy(s: OrthancStudyInfo): StudyItem {
  const tags = s.MainDicomTags || ({} as any);
  const ptags = s.PatientMainDicomTags || ({} as any);
  const patientName = ptags.PatientName || tags.PatientName || 'N/A';
  const lastUpdateStr = s.LastUpdate ?? s.Metadata?.LastUpdate ?? '';
  const lastUpdateMs = parseOrthancDate(lastUpdateStr);

  return {
    studyInstanceUid: tags.StudyInstanceUID || s.ID,
    orthancId: s.ID,
    patientName,
    patientId: ptags.PatientID || tags.PatientID || '',
    studyDate: tags.StudyDate || '',
    studyTime: tags.StudyTime || '',
    studyDescription: tags.StudyDescription || '',
    modality: tags.Modality || '',
    lastUpdate: lastUpdateStr,
    lastUpdateMs,
    saves: [],
    savesCount: 0,
    isLoadingSaves: false,
  };
}

// Sorted studies based on Import Date (LastUpdate)
const sortedStudies = computed<StudyItem[]>(() => {
  const list = [...allDicomStudies.value];
  list.sort((a, b) => {
    if (sortOrder.value === 'newest') {
      return b.lastUpdateMs - a.lastUpdateMs;
    }
    return a.lastUpdateMs - b.lastUpdateMs;
  });
  return list;
});

// Paginated studies (5 per page)
const paginatedStudies = computed<StudyItem[]>(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return sortedStudies.value.slice(start, start + PAGE_SIZE);
});

// Total pages calculation
const totalPages = computed(() => {
  return Math.max(1, Math.ceil(sortedStudies.value.length / PAGE_SIZE));
});

// Load studies from DICOM (Orthanc) & all Supabase saves for the visible studies
async function load() {
  try {
    busy.value = true;

    // 1. Fetch DICOM studies from Orthanc
    let orthancStudies: OrthancStudyInfo[] = [];
    try {
      orthancStudies = await orthancStore.fetchStudies();
    }
    catch (e) {
      console.warn('Could not fetch Orthanc studies:', e);
    }

    const studyList = orthancStudies.map(flattenStudy);

    // 2. Fetch analysis_state metadata from Supabase (fetching title from JSON without pulling full state payload)
    const { data: dbSaves, error } = await supabase
      .from('analysis_state')
      .select(`
        id,
        study_instance_uid,
        user,
        userMeta:person!analysis_state_user_fkey(card),
        org,
        orgMeta:org!analysis_state_org_fkey(
          person(card)
        ),
        title:state->>title,
        created_at
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const savesMap = new Map<string, AnalysisSaveItem[]>();
    (dbSaves || []).forEach((row: any) => {
      const userCard = row.userMeta?.card || {};
      const orgCard = row.orgMeta?.person?.card || {};

      const userName = userCard.fn || userCard.email || 'Unknown User';
      const userEmail = userCard.email || '';
      const orgName = orgCard.fn || 'Default Organization';
      const title = row.title || 'Untitled Save';

      const saveItem: AnalysisSaveItem = {
        id: row.id,
        study_instance_uid: row.study_instance_uid,
        user: row.user,
        userName,
        userEmail,
        org: row.org,
        orgName,
        title,
        created_at: row.created_at,
      };

      const list = savesMap.get(row.study_instance_uid) || [];
      list.push(saveItem);
      savesMap.set(row.study_instance_uid, list);
    });

    savesByStudyUid.value = savesMap;

    // Check if there are studies in Supabase not present in Orthanc
    const knownUids = new Set(studyList.map(s => s.studyInstanceUid));
    savesMap.forEach((saves, uid) => {
      if (!knownUids.has(uid)) {
        const latestDate = saves[0]?.created_at || '';
        studyList.push({
          studyInstanceUid: uid,
          orthancId: '',
          patientName: `Archived Study (${uid.slice(0, 10)}...)`,
          patientId: '',
          studyDate: '',
          studyTime: '',
          studyDescription: '',
          modality: '',
          lastUpdate: latestDate,
          lastUpdateMs: parseOrthancDate(latestDate),
          saves: [],
          savesCount: 0,
          isLoadingSaves: false,
        });
      }
    });

    // Attach saves to all studies
    studyList.forEach((s) => {
      const studySaves = savesMap.get(s.studyInstanceUid) || [];
      s.saves = studySaves;
      s.savesCount = studySaves.length;
    });

    allDicomStudies.value = studyList;

    // Maintain or initialize study selection
    if (selectedStudyUid.value && !studyList.some(s => s.studyInstanceUid === selectedStudyUid.value)) {
      selectedStudyUid.value = paginatedStudies.value[0]?.studyInstanceUid || null;
    }
    else if (!selectedStudyUid.value && paginatedStudies.value.length > 0) {
      selectedStudyUid.value = paginatedStudies.value[0].studyInstanceUid;
    }

    // Clean up selected save IDs
    const validIds = new Set((dbSaves || []).map((s: any) => s.id));
    selectedSaveIds.value = new Set(
      Array.from(selectedSaveIds.value).filter(id => validIds.has(id)),
    );
  }
  catch (error) {
    console.error('Error loading analysis states:', error);
    emit('error', getMessage(error));
    alert.error(`Failed to load studies: ${getMessage(error)}`);
  }
  finally {
    busy.value = false;
  }
}

defineExpose({ load });
onMounted(load);

// Auto-select first study when page changes if current selection is not on this page
watch(paginatedStudies, (newList) => {
  if (newList.length > 0) {
    const isCurrentOnPage = newList.some(s => s.studyInstanceUid === selectedStudyUid.value);
    if (!isCurrentOnPage) {
      selectedStudyUid.value = newList[0].studyInstanceUid;
    }
  }
  else {
    selectedStudyUid.value = null;
  }
});

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'newest' ? 'oldest' : 'newest';
  currentPage.value = 1;
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
}

// Currently active study
const activeStudy = computed<StudyItem | null>(() => {
  if (!selectedStudyUid.value) return null;
  return allDicomStudies.value.find(s => s.studyInstanceUid === selectedStudyUid.value) || null;
});

// All historical saves for the currently active study
const activeStudySaves = computed<AnalysisSaveItem[]>(() => {
  if (!selectedStudyUid.value) return [];
  return savesByStudyUid.value.get(selectedStudyUid.value) || [];
});

// Selection helpers
const areAllSelected = computed(() => {
  if (activeStudySaves.value.length === 0) return false;
  return activeStudySaves.value.every(s => selectedSaveIds.value.has(s.id));
});

const isSomeSelected = computed(() => {
  return (
    activeStudySaves.value.some(s => selectedSaveIds.value.has(s.id))
    && !areAllSelected.value
  );
});

const selectedCountForActiveStudy = computed(() => {
  return activeStudySaves.value.filter(s => selectedSaveIds.value.has(s.id)).length;
});

function toggleSelectAll() {
  if (areAllSelected.value) {
    activeStudySaves.value.forEach(s => selectedSaveIds.value.delete(s.id));
  }
  else {
    activeStudySaves.value.forEach(s => selectedSaveIds.value.add(s.id));
  }
}

function toggleSaveSelection(id: string) {
  if (selectedSaveIds.value.has(id)) {
    selectedSaveIds.value.delete(id);
  }
  else {
    selectedSaveIds.value.add(id);
  }
}

function selectStudy(uid: string) {
  selectedStudyUid.value = uid;
}

// Single Delete
function openDeleteSingleDialog(save: AnalysisSaveItem) {
  saveToDelete.value = save;
  dialogDeleteSingle.value = true;
}

async function confirmDeleteSingle() {
  if (!saveToDelete.value) return;
  const target = saveToDelete.value;

  try {
    isDeleting.value = true;
    const { count, error } = await supabase
      .from('analysis_state')
      .delete({ count: 'exact' })
      .eq('id', target.id);

    if (error) throw error;
    if (count === 0) throw new Error('No records were deleted');

    alert.info(`Deleted analysis state: "${target.title}"`);
    selectedSaveIds.value.delete(target.id);
    dialogDeleteSingle.value = false;
    saveToDelete.value = null;
    await load();
  }
  catch (error) {
    console.error('Error deleting analysis state:', error);
    alert.error(`Failed to delete save: ${getMessage(error)}`);
  }
  finally {
    isDeleting.value = false;
  }
}

// Batch Delete
function openDeleteBatchDialog() {
  if (selectedCountForActiveStudy.value === 0) return;
  dialogDeleteBatch.value = true;
}

async function confirmDeleteBatch() {
  const idsToDelete = activeStudySaves.value
    .filter(s => selectedSaveIds.value.has(s.id))
    .map(s => s.id);

  if (idsToDelete.length === 0) return;

  try {
    isDeleting.value = true;
    const { count, error } = await supabase
      .from('analysis_state')
      .delete({ count: 'exact' })
      .in('id', idsToDelete);

    if (error) throw error;

    alert.info(`Successfully deleted ${count ?? idsToDelete.length} analysis state(s).`);
    idsToDelete.forEach(id => selectedSaveIds.value.delete(id));
    dialogDeleteBatch.value = false;
    await load();
  }
  catch (error) {
    console.error('Error deleting analysis states in batch:', error);
    alert.error(`Failed to delete saves: ${getMessage(error)}`);
  }
  finally {
    isDeleting.value = false;
  }
}

// Delete All for Study
function openDeleteStudyDialog() {
  if (!activeStudy.value) return;
  dialogDeleteStudy.value = true;
}

async function confirmDeleteStudy() {
  if (!activeStudy.value) return;
  const idsToDelete = activeStudySaves.value.map(s => s.id);

  try {
    isDeleting.value = true;
    const { count, error } = await supabase
      .from('analysis_state')
      .delete({ count: 'exact' })
      .in('id', idsToDelete);

    if (error) throw error;

    alert.info(
      `Successfully deleted all ${count ?? idsToDelete.length} saves for study ${activeStudy.value.patientName}.`,
    );
    idsToDelete.forEach(id => selectedSaveIds.value.delete(id));
    dialogDeleteStudy.value = false;
    await load();
  }
  catch (error) {
    console.error('Error deleting all saves for study:', error);
    alert.error(`Failed to delete saves: ${getMessage(error)}`);
  }
  finally {
    isDeleting.value = false;
  }
}

// Delete Entire Study (Orthanc DICOM study + all Supabase saves)
function openDeleteEntireStudyDialog(study?: StudyItem) {
  studyToDelete.value = study || activeStudy.value;
  if (!studyToDelete.value) return;
  dialogDeleteEntireStudy.value = true;
}

async function confirmDeleteEntireStudy() {
  if (!studyToDelete.value) return;
  const target = studyToDelete.value;

  try {
    isDeleting.value = true;

    // 1. Delete all saved analysis states from Supabase for this study Instance UID
    const { error: dbError } = await supabase
      .from('analysis_state')
      .delete()
      .eq('study_instance_uid', target.studyInstanceUid);

    if (dbError) throw dbError;

    // 2. Delete study from Orthanc if present
    if (target.orthancId) {
      await orthancStore.deleteStudies([target.orthancId]);
    }

    alert.info(
      `Successfully deleted study "${target.patientName}" from Orthanc and removed all associated saved states.`,
    );

    target.saves.forEach(s => selectedSaveIds.value.delete(s.id));
    if (selectedStudyUid.value === target.studyInstanceUid) {
      selectedStudyUid.value = null;
    }

    dialogDeleteEntireStudy.value = false;
    studyToDelete.value = null;
    await load();
  }
  catch (error) {
    console.error('Error deleting study and saves:', error);
    alert.error(`Failed to delete study: ${getMessage(error)}`);
  }
  finally {
    isDeleting.value = false;
  }
}
</script>

<template>
  <div class="flex flex-column">
    <!-- Top Action & Pagination Bar -->
    <v-toolbar density="comfortable" color="transparent" flat class="px-0 mb-3">
      <!-- Sort Order Toggle -->
      <v-btn
        variant="outlined"
        color="primary"
        size="small"
        class="me-3 font-weight-medium"
        :prepend-icon="
          sortOrder === 'newest'
            ? 'mdi-sort-clock-descending-outline'
            : 'mdi-sort-clock-ascending-outline'
        "
        @click="toggleSortOrder"
      >
        Sort By: {{ sortOrder === 'newest' ? 'Most Recent First' : 'Oldest First' }}
      </v-btn>

      <v-chip variant="text" size="small" class="me-2">
        <v-icon start icon="mdi-folder-multiple-outline" size="16" />
        {{ allDicomStudies.length }} Total Studies
      </v-chip>

      <v-spacer />

      <v-btn
        :loading="busy"
        icon="mdi-refresh"
        variant="text"
        color="primary"
        title="Refresh studies & saves"
        @click="load"
      />
    </v-toolbar>

    <!-- Main Master-Detail View -->
    <div class="split-pane-container">
      <!-- Left Column: 5 Studies List -->
      <v-card variant="outlined" class="study-list-pane">
        <v-card-item class="py-2 px-3 border-b flex align-center justify-space-between">
          <!-- Pagination Controls (5 per page) -->
          <div class="d-flex align-center ga-1 me-4">
            <v-btn
              icon="mdi-chevron-left"
              size="small"
              variant="outlined"
              density="comfortable"
              :disabled="currentPage <= 1 || busy"
              title="Previous 5 Studies"
              @click="prevPage"
            />

            <span class="text-body-small font-weight-medium px-2">
              Page {{ currentPage }} of {{ totalPages }}
              <span class="text-medium-emphasis">
                ({{ (currentPage - 1) * PAGE_SIZE + 1 }}-{{ Math.min(currentPage * PAGE_SIZE, sortedStudies.length) }} of {{ sortedStudies.length }})
              </span>
            </span>

            <v-btn
              icon="mdi-chevron-right"
              size="small"
              variant="outlined"
              density="comfortable"
              :disabled="currentPage >= totalPages || busy"
              title="Next 5 Studies"
              @click="nextPage"
            />
          </div>
        </v-card-item>

        <v-progress-linear v-if="busy" indeterminate color="primary" height="2" />

        <div v-if="paginatedStudies.length === 0 && !busy" class="empty-list-placeholder">
          <v-icon icon="mdi-database-remove-outline" size="48" color="medium-emphasis" class="mb-2" />
          <div class="text-body-medium text-medium-emphasis">
            No studies available
          </div>
        </div>

        <v-list v-else lines="two" class="study-items-list pa-2" density="comfortable">
          <v-list-item
            v-for="study in paginatedStudies"
            :key="study.studyInstanceUid"
            :active="selectedStudyUid === study.studyInstanceUid"
            rounded="lg"
            class="mb-2 study-list-item"
            color="primary"
            @click="selectStudy(study.studyInstanceUid)"
          >
            <v-list-item-title class="font-weight-bold text-body-medium d-flex align-center justify-space-between">
              <span class="text-truncate" :title="study.patientName">
                {{ study.patientName }}
              </span>
              <div class="d-flex align-center ga-1">
                <v-chip
                  size="x-small"
                  :color="study.savesCount > 0 ? 'primary' : 'grey'"
                  class="ms-2 font-weight-bold"
                >
                  {{ study.savesCount }} {{ study.savesCount === 1 ? 'save' : 'saves' }}
                </v-chip>
                <v-btn
                  v-if="study.orthancId"
                  icon="mdi-delete"
                  size="x-small"
                  variant="text"
                  color="error"
                  density="comfortable"
                  title="Delete study from Orthanc & saved data"
                  :loading="isDeleting && studyToDelete?.studyInstanceUid === study.studyInstanceUid"
                  @click.stop="openDeleteEntireStudyDialog(study)"
                />
              </div>
            </v-list-item-title>

            <v-list-item-subtitle class="text-body-small mt-1">
              <div class="text-truncate font-weight-medium">
                <span v-if="study.studyDescription">{{ study.studyDescription }}</span>
                <span v-else-if="study.modality">{{ study.modality }} Study</span>
                <span v-if="study.studyDate" class="text-medium-emphasis"> · {{ formatDicomDate(study.studyDate) }}</span>
              </div>
              <div class="d-flex align-center justify-space-between text-body-small text-medium-emphasis mt-1">
                <span v-if="study.patientId">ID: {{ study.patientId }}</span>
                <span v-else class="font-mono">UID: {{ study.studyInstanceUid.slice(0, 12) }}...</span>
                <span>Imported: {{ formatImportDate(study.lastUpdate) }}</span>
              </div>
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card>

      <!-- Right Column: Selected Study Details & Saves Table -->
      <v-card variant="outlined" class="saves-detail-pane">
        <template v-if="activeStudy">
          <!-- Active Study Header -->
          <div class="pa-4 border-b">
            <div class="d-flex align-center justify-space-between flex-wrap ga-2">
              <div>
                <div class="d-flex align-center ga-2 flex-wrap">
                  <h5 class="font-weight-bold mb-0">
                    {{ activeStudy.patientName }}
                  </h5>
                  <v-chip v-if="activeStudy.studyDate" size="x-small" variant="outlined">
                    Study Date: {{ formatDicomDate(activeStudy.studyDate) }}
                  </v-chip>
                  <v-chip v-if="activeStudy.patientId" size="x-small" variant="tonal" color="info">
                    Patient ID: {{ activeStudy.patientId }}
                  </v-chip>
                </div>

                <div v-if="activeStudy.studyDescription" class="text-body-medium text-medium-emphasis mt-1">
                  {{ activeStudy.studyDescription }}
                </div>
              </div>

              <!-- Action Buttons -->
              <div v-if="activeStudySaves.length > 0 || activeStudy.orthancId" class="d-flex align-center ga-2 flex-wrap">
                <template v-if="activeStudySaves.length > 0">
                  <v-btn
                    size="small"
                    variant="outlined"
                    density="comfortable"
                    prepend-icon="mdi-checkbox-multiple-marked-outline"
                    @click="toggleSelectAll"
                  >
                    {{ areAllSelected ? 'Deselect All' : 'Select All' }}
                  </v-btn>

                  <v-btn
                    v-if="selectedCountForActiveStudy > 0"
                    size="small"
                    color="error"
                    variant="flat"
                    density="comfortable"
                    prepend-icon="mdi-delete"
                    :loading="isDeleting"
                    @click="openDeleteBatchDialog"
                  >
                    Delete Selected ({{ selectedCountForActiveStudy }})
                  </v-btn>

                  <v-btn
                    size="small"
                    color="error"
                    variant="tonal"
                    density="comfortable"
                    prepend-icon="mdi-trash-can-outline"
                    :loading="isDeleting"
                    @click="openDeleteStudyDialog"
                  >
                    Delete All ({{ activeStudySaves.length }})
                  </v-btn>
                </template>
              </div>
            </div>
          </div>

          <!-- Saves Table or Empty State -->
          <div v-if="activeStudySaves.length === 0" class="empty-detail-placeholder">
            <v-icon icon="mdi-content-save-off-outline" size="52" color="medium-emphasis" class="mb-3" />
            <div class="text-headline-small font-weight-medium text-medium-emphasis">
              No Saved Analysis States
            </div>
            <div class="text-body-medium text-medium-emphasis mt-1">
              There are currently no saved analysis states in storage for this study.
            </div>
          </div>

          <v-table v-else hover density="comfortable" class="saves-table">
            <thead>
              <tr>
                <th style="width: 48px" class="text-center">
                  <v-checkbox-btn
                    :model-value="areAllSelected"
                    :indeterminate="isSomeSelected"
                    density="compact"
                    hide-details
                    @update:model-value="toggleSelectAll"
                  />
                </th>
                <th>Save Title</th>
                <th>Saved By</th>
                <th>Created At</th>
                <th class="text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="save in activeStudySaves"
                :key="save.id"
                :class="{ 'selected-row': selectedSaveIds.has(save.id) }"
              >
                <td class="text-center">
                  <v-checkbox-btn
                    :model-value="selectedSaveIds.has(save.id)"
                    density="compact"
                    hide-details
                    @click.stop="toggleSaveSelection(save.id)"
                  />
                </td>
                <td>
                  <div class="font-weight-medium text-body-medium">
                    {{ save.title }}
                  </div>
                  <div class="text-body-small text-medium-emphasis font-mono">
                    ID: {{ save.id.slice(0, 8) }}...
                  </div>
                </td>
                <td>
                  <div class="text-body-medium">
                    {{ save.userName }}
                  </div>
                  <div v-if="save.userEmail && save.userEmail !== save.userName" class="text-body-small text-medium-emphasis">
                    {{ save.userEmail }}
                  </div>
                </td>
                <td>
                  <span class="text-body-medium">{{ formatDate(save.created_at) }}</span>
                </td>
                <td class="text-right">
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    density="comfortable"
                    color="error"
                    title="Delete Save"
                    :loading="isDeleting && saveToDelete?.id === save.id"
                    @click.stop="openDeleteSingleDialog(save)"
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <!-- No Study Selected -->
        <div v-else class="empty-detail-placeholder">
          <v-icon icon="mdi-cursor-default-click-outline" size="56" color="medium-emphasis" class="mb-3" />
          <div class="text-headline-small font-weight-medium text-medium-emphasis">
            Select a Study
          </div>
          <div class="text-body-medium text-medium-emphasis mt-1">
            Choose a study from the left list to view and manage its saved analysis states.
          </div>
        </div>
      </v-card>
    </div>

    <!-- Confirm Single Delete Dialog -->
    <v-dialog v-model="dialogDeleteSingle" max-width="500px">
      <v-card v-if="saveToDelete">
        <v-card-title class="text-headline-small d-flex align-center">
          <v-icon icon="mdi-alert" color="error" class="me-2" />
          Delete Analysis State
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Are you sure you want to delete the analysis state:
          </p>
          <div class="pa-3 rounded mb-3">
            <div class="font-weight-bold">
              {{ saveToDelete.title }}
            </div>
            <div class="text-body-small text-medium-emphasis">
              User: {{ saveToDelete.userName }}
            </div>
            <div class="text-body-small text-medium-emphasis">
              Saved: {{ formatDate(saveToDelete.created_at) }}
            </div>
          </div>
          <p class="text-body-small text-error mb-0">
            This action cannot be undone. All contours and analysis results associated with this save will be permanently deleted.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isDeleting" @click="dialogDeleteSingle = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" :loading="isDeleting" @click="confirmDeleteSingle">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Batch Delete Dialog -->
    <v-dialog v-model="dialogDeleteBatch" max-width="500px">
      <v-card>
        <v-card-title class="text-headline-small d-flex align-center">
          <v-icon icon="mdi-alert" color="error" class="me-2" />
          Delete Selected Analysis States
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Are you sure you want to delete <strong>{{ selectedCountForActiveStudy }}</strong> selected analysis state(s)?
          </p>
          <p class="text-body-small text-error mb-0">
            This action cannot be undone. All contours and analysis results associated with these saves will be permanently removed.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isDeleting" @click="dialogDeleteBatch = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" :loading="isDeleting" @click="confirmDeleteBatch">
            Delete {{ selectedCountForActiveStudy }} Saves
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete All Study Saves Dialog -->
    <v-dialog v-model="dialogDeleteStudy" max-width="520px">
      <v-card v-if="activeStudy">
        <v-card-title class="text-headline-small d-flex align-center">
          <v-icon icon="mdi-alert-octagon" color="error" class="me-2" />
          Delete All Saves for Study
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Are you sure you want to delete <strong>ALL {{ activeStudySaves.length }}</strong> saved analysis states for:
          </p>
          <div class="pa-3 rounded bg-surface-variant mb-3 font-weight-medium">
            <div>{{ activeStudy.patientName }}</div>
            <div class="text-body-small text-medium-emphasis font-mono text-truncate">
              UID: {{ activeStudy.studyInstanceUid }}
            </div>
          </div>
          <p class="text-body-small text-error mb-0">
            This will permanently remove all analysis states, contours, and results stored for this study across all users.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isDeleting" @click="dialogDeleteStudy = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" :loading="isDeleting" @click="confirmDeleteStudy">
            Delete All Saves
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Entire Study (Orthanc + Saves) Dialog -->
    <v-dialog v-model="dialogDeleteEntireStudy" max-width="520px">
      <v-card v-if="studyToDelete">
        <v-card-title class="text-headline-small d-flex align-center">
          <v-icon icon="mdi-alert-octagon" color="error" class="me-2" />
          Delete DICOM Study & All Save Data
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Are you sure you want to permanently delete this study from Orthanc:
          </p>
          <div class="pa-3 rounded mb-3 font-weight-medium">
            <div>{{ studyToDelete.patientName }}</div>
            <div v-if="studyToDelete.studyDescription" class="text-body-small text-medium-emphasis">
              {{ studyToDelete.studyDescription }}
            </div>
            <div class="text-body-small text-medium-emphasis font-mono text-truncate">
              UID: {{ studyToDelete.studyInstanceUid }}
            </div>
          </div>
          <p v-if="studyToDelete.savesCount > 0" class="text-body-small text-error font-weight-bold mb-2">
            Warning: This will also permanently remove all {{ studyToDelete.savesCount }} saved analysis state(s) for this study.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isDeleting" @click="dialogDeleteEntireStudy = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" :loading="isDeleting" @click="confirmDeleteEntireStudy">
            Delete Study
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.split-pane-container {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 16px;
  min-height: 520px;
  height: calc(100vh - 360px);
}

@media (max-width: 960px) {
  .split-pane-container {
    grid-template-columns: 1fr;
    height: auto;
  }
}

.study-list-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--md-on-surface-variant) !important;
}

.study-items-list {
  flex: 1;
  overflow-y: auto;
}

.study-list-item {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.saves-detail-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--md-on-surface-variant) !important;
}

.saves-table {
  flex: 1;
  overflow-y: auto;
}

.selected-row {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.empty-list-placeholder,
.empty-detail-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.json-preview {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  font-family: monospace;
  font-size: 11px;
  max-height: 250px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.font-mono {
  font-family: monospace;
}
</style>
